import type { BillingInterval, BillingPlan, PlanChoice } from 'definitions/billing-plans';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { readApiError } from '@/state/billing/api-error';
import { BILLING_PLAN_CATALOGUE } from 'definitions/billing-plans';
import { describeChoice } from '@/state/billing/plan-summary';
import { useBillingState } from '@/state/billing/useBillingState';
import { useCheckout } from '@/state/billing/useCheckout';
import { parseWebsiteAddress } from '@/state/onboarding/website-address';

type CreatedCompany = { id: number };

type UseAddClientParams = { onDone: () => void };

/**
 * Adding a client: its name and website, then (on a card account) the plan it is on, which is
 * summarised and accepted before anything is created or charged, then the company, its website
 * and Polar's checkout for its plan. A client has a plan of its own, paid on the agency's
 * billing account, so it is chosen when the client is added and not left for later.
 *
 * An agency that is not billed by card (invoice or free) chooses the plan too, but as a label:
 * the client inherits the agency's mode, so nothing is bought and no checkout opens. The plan is
 * what the client's sessions are measured against, and Pertento can change it later.
 *
 * The client is created before the checkout opens, and a checkout left unfinished leaves a
 * client with no plan (read-only until the agency chooses one from Settings, Billing, or the
 * Clients table). Nothing is saved until the last step is accepted.
 */
export const useAddClient = ({ onDone }: UseAddClientParams) => {
  const apiClient = useClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { billingState } = useBillingState();
  const checkout = useCheckout();

  const [step, setStep] = useState<'details' | 'plan'>('details');
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [touched, setTouched] = useState(false);
  const [interval, setInterval] = useState<BillingInterval>('year');
  const [picked, setPicked] = useState<BillingPlan | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [billing, setBilling] = useState<'card' | 'invoice'>('card');

  const address = parseWebsiteAddress(url);
  const mode = billingState?.billingMode;
  const isCardAgency = mode === 'card';
  // An agency billed by invoice or free: its clients inherit that, so their plan is a label with no checkout.
  const offCard = mode === 'invoice' || mode === 'free';
  // Every client gets its plan here, so the details cannot be continued until the agency's mode is
  // known: before /billing/state answers (or when it failed) a client would otherwise be created with
  // no plan and no checkout, serving nothing, behind a success toast.
  const modeKnown = isCardAgency || offCard;
  // Only a Super Admin can bill a client by invoice, and only where the agency itself is on card: an
  // agency that is not has no card plan to avoid. The server refuses anyone else.
  const canInvoice = isCardAgency && user?.role === 'Super Admin';
  const isInvoice = canInvoice && billing === 'invoice';
  /** The plan is recorded, not bought: no checkout follows. */
  const labelOnly = isInvoice || offCard;
  const hasDetails = !!name.trim() && !!address;
  const choice: PlanChoice | null = picked ? { plan: picked, interval } : null;

  const summary = choice
    ? labelOnly
      ? {
          title: `${BILLING_PLAN_CATALOGUE[choice.plan].name} for ${name.trim()}.`,
          line:
            mode === 'free'
              ? 'A free account, so nothing is charged and nothing goes to Polar.'
              : 'Billed by invoice, so no card is charged and nothing goes to Polar.',
          rows: [
            {
              when: 'Today',
              what: `${name.trim()} is added on ${BILLING_PLAN_CATALOGUE[choice.plan].name}`,
              amount: mode === 'free' ? 'Free' : 'Invoice',
              emphasis: true,
            },
          ],
          ctaLabel: `Add ${name.trim()}${mode === 'free' ? '' : ' by invoice'}`,
          note: isInvoice
            ? 'Only a Super Admin can do this. The client is never charged by card.'
            : 'The plan is what its sessions are measured against. Pertento can change it for you later.',
        }
      : describeChoice({
          choice,
          direction: null,
          hasPlan: false,
          isTrialing: false,
          trialAvailable: !!billingState?.trialAvailable,
          trialEnd: null,
          currentPeriodEnd: null,
          currentName: null,
          checkoutAvailable: billingState?.checkoutAvailable ?? false,
          forName: name.trim(),
        })
    : null;

  const reset = () => {
    setStep('details');
    setName('');
    setUrl('');
    setTouched(false);
    setInterval('year');
    setPicked(null);
    setBilling('card');
  };

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['CLIENT_ACCOUNTS'] }),
      queryClient.invalidateQueries({
        predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('BILLING_'),
      }),
    ]);

  /** The details step's button: on to the plan. */
  const continueFromDetails = () => {
    setTouched(true);
    if (!hasDetails || !modeKnown) return;
    setStep('plan');
  };

  const create = async () => {
    if (!user || !address || isSaving || !choice) return;
    setIsSaving(true);
    let companyId: number | null = null;
    try {
      const company = await apiClient.post<CreatedCompany>('/companies', {
        name: name.trim(),
        parentCompanyId: user.company.id,
        ...(isInvoice ? { billingMode: 'invoice' } : {}),
        ...(labelOnly ? { plan: choice.plan, interval: choice.interval } : {}),
      });
      companyId = company.id;
      await apiClient.post('/websites', { companyId, url: address.href });
    } catch (error) {
      const { message } = readApiError(error);
      toast.error(companyId ? 'The client was added, but its website was not saved' : 'Could not add the client', {
        description: message ?? (companyId ? 'Add the website from Websites.' : 'Please try again in a moment.'),
      });
      if (companyId) {
        await refresh();
        reset();
        onDone();
      }
      setIsSaving(false);
      return;
    }

    await refresh();
    if (!labelOnly) {
      // To Polar, for this client's plan. Left pending on purpose: the page is navigating away.
      const started = await checkout.startCheckout({
        plan: choice.plan,
        interval: choice.interval,
        accountId: companyId,
      });
      if (started) return;
      // Checkout did not open (it says why in its own toast). The client exists, so close the dialog as
      // the website failure does, rather than leave it frozen with a way to create the client twice.
      toast.info(`${name.trim()} was added without a plan`, {
        description: 'Choose one from Settings, Billing, Clients.',
      });
      reset();
      onDone();
      setIsSaving(false);
      return;
    }
    toast.success(`${name.trim()} was added.`);
    reset();
    onDone();
    setIsSaving(false);
  };

  return {
    step,
    name,
    setName,
    url,
    setUrl: (value: string) => {
      setUrl(value);
      setTouched(true);
    },
    isUrlInvalid: touched && !!url.trim() && !address,
    /** False until the agency's billing mode has been read: the plan step depends on it. */
    modeKnown,
    canContinue: hasDetails && !isSaving && modeKnown,
    continueFromDetails,
    back: () => setStep('details'),
    interval,
    setInterval,
    picked,
    pick: setPicked,
    clearPick: () => setPicked(null),
    summary,
    /** Whether a plan can be bought by card here at all (Polar set up). */
    checkoutAvailable: billingState?.checkoutAvailable ?? false,
    canAccept: !!summary && !isSaving && (labelOnly || (billingState?.checkoutAvailable ?? false)),
    canInvoice,
    /** The agency is billed by invoice or free, so the plan is a label and no checkout follows. */
    offCard,
    labelOnly,
    billing,
    setBilling,
    accept: create,
    isSaving: isSaving || checkout.isStarting,
    cancel: () => {
      if (isSaving) return;
      reset();
      onDone();
    },
  };
};

export type AddClientManager = ReturnType<typeof useAddClient>;
